import { useTranslation } from "react-i18next"
import {
  getEstimateYearlySalary,
  getIsHourlySalary,
  getOneTimePaymentMonthSummary,
  useSalaryPaymentsNum,
  useVarCompSalaryEstimation,
} from "@oysterhr/hiring/full-time-employee"
import { PaymentFrequencyEnum } from "@oysterhr/schema"
import {
  getAmountFromPercentageOfGross,
  calculateAmount,
  formatMoney,
  paidInMonths,
  paymentsCount,
  buildMoney,
  getMonths,
} from "./helpers"
import { VariableCompensationSummaryProps } from "./VariableCompensationSummary"

export const useVariableCompensationSummary = (props: VariableCompensationSummaryProps) => {
  const { t } = useTranslation("variable_compensation/Common")
  const isPercentage = props.type === "percentage"
  const oneTimePayment = PaymentFrequencyEnum.OneTime
  const yearly = PaymentFrequencyEnum.Yearly
  const months = getMonths(props.frequency, props.selectedMonths)
  const hasMonths = months.length > 0 ? true : undefined

  const { data } = useVarCompSalaryEstimation({ employmentId: props.employmentId as string })
  const employment = data?.employment
  const salaryPaymentsNum = useSalaryPaymentsNum(employment?.countryCode)
  const isHourlySalary = getIsHourlySalary(employment)

  const estimatedYearlySalarySubunit = isPercentage
    ? getEstimateYearlySalary({
        ...employment,
        salaryAmount: props.salary.subunit,
        numberOfPayments: salaryPaymentsNum,
        salaryPeriod: props.salaryPeriod,
      })
    : null

  function skipForFrequency<T>(frequency: PaymentFrequencyEnum | PaymentFrequencyEnum[], value: T) {
    return !(Array.isArray(frequency)
      ? frequency.includes(props.frequency)
      : props.frequency === frequency)
      ? value
      : undefined
  }

  const commonTitles = {
    periodic:
      hasMonths &&
      skipForFrequency(oneTimePayment, paymentsCount(t, props.frequency, props.selectedMonths)),
    periodicDescription: hasMonths && skipForFrequency(oneTimePayment, paidInMonths(t, props)),
    annual:
      oneTimePayment === props.frequency
        ? t("GENERIC.summary.oneTimeOnFirstPayment")
        : t("GENERIC.summary.yearlyCost"),
  }

  if (isPercentage) {
    const yearlySalaryMoney = buildMoney({
      currencyCode: props.salary.currencyCode,
      subunit: isHourlySalary ? estimatedYearlySalarySubunit : props.salary.subunit,
    })
    const { perPeriodAmount, annualAmount } = getAmountFromPercentageOfGross(
      yearlySalaryMoney,
      props.percentage,
      props.frequency,
      props.selectedMonths,
    )

    return {
      titles: {
        ...commonTitles,
        annualDescription:
          oneTimePayment === props.frequency
            ? getOneTimePaymentMonthSummary(props.effectiveDate, props.startDate, t)
            : [yearly].includes(props.frequency)
              ? t("GENERIC.summary.percentageOfGross", { percentage: props.percentage })
              : undefined,
      },
      values: {
        periodic: hasMonths && skipForFrequency(oneTimePayment, formatMoney(perPeriodAmount)),
        periodicDescription:
          hasMonths &&
          skipForFrequency(
            [oneTimePayment, yearly],
            t("GENERIC.summary.percentageOfGross", {
              percentage: props.percentage,
            }),
          ),
        annual: formatMoney(annualAmount),
        annualDescription:
          oneTimePayment === props.frequency
            ? t("GENERIC.summary.percentageOfGross", {
                percentage: props.percentage,
              })
            : undefined,
      },
      /**
       * Only applicable for percentage variable compensations.
       *
       * This is the _estimated_ yearly salary if the salary period is "hourly";
       * Otherwise, this is the yearly gross salary.
       */
      yearlySalaryMoney,
    }
  } else {
    const { perPeriodAmount, annualAmount } = calculateAmount(
      buildMoney(props.amount),
      props.frequency,
      props.selectedMonths,
    )
    return {
      titles: {
        ...commonTitles,
        annualDescription:
          oneTimePayment === props.frequency
            ? getOneTimePaymentMonthSummary(props.effectiveDate, props.startDate, t)
            : undefined,
      },
      values: {
        periodic: hasMonths && skipForFrequency(oneTimePayment, formatMoney(perPeriodAmount)),
        periodicDescription: undefined,
        annual: formatMoney(annualAmount),
      },

      templateAmount:
        props?.templateAmount && props?.templateAmount?.currencyCode !== props?.amount?.currencyCode
          ? formatMoney(buildMoney(props.templateAmount))
          : undefined,
    }
  }
}
